export const MAX_COMPARISON_MODELS = 25;
export type Obj = Record<string, any>;
export type Connection = Obj & {
  id: string;
  name: string;
  provider: string;
  keys: Obj[];
};
export type Model = Obj & {
  id: string;
  name: string;
  connection_id: string;
  model_id: string;
};
export type Prompt = Obj & {
  id: string;
  name: string;
  content?: string;
  content_length?: number;
  detail_loaded?: boolean;
  version: number;
  campaign: string;
  language: string;
};
export type Suite = Obj & {
  id: string;
  name: string;
  cases: Obj[];
  campaign: string;
  language: string;
};
export type Bootstrap = {
  connections: Connection[];
  models: Model[];
  prompts: Prompt[];
  suites: Suite[];
  runs: Obj[];
  system: Obj;
};
export async function request<T = Obj>(
  path: string,
  body?: unknown,
  method?: string,
): Promise<T> {
  const r = await fetch("/api/bench" + path, {
    method: method || (body === undefined ? "GET" : "POST"),
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = await r.json().catch(() => ({
    detail: "The local service returned an unreadable response.",
  }));
  if (r.status === 401 && location.pathname !== "/login")
    location.assign("/login?next=" + encodeURIComponent(location.pathname));
  if (!r.ok)
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : JSON.stringify(data.detail || data),
    );
  return data as T;
}
export const money = (v: unknown) =>
  typeof v === "number"
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: v < 0.01 ? 5 : 3,
      }).format(v)
    : "—";
export const duration = (v: unknown) =>
  typeof v === "number"
    ? v < 1000
      ? Math.round(v) + " ms"
      : (v / 1000).toFixed(2) + " s"
    : "—";
export const number = (v: unknown) =>
  typeof v === "number"
    ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(v)
    : "—";
export const label = (v: unknown) =>
  String(v ?? "")
    .replaceAll("_", " ")
    .replace(/^./, (x) => x.toUpperCase());
export const evaluation = (c: Obj | undefined) =>
  c?.effective_evaluation || c?.evaluation || { status: "ungraded" };
export const checkpointFindings = (c: Obj | undefined) => {
  const e = evaluation(c);
  const checkpoints: Obj[] = e.checkpoint_evaluations || [];
  return {
    graded:
      e.graded_checkpoints ??
      checkpoints.filter((x) => x.status === "evaluated").length,
    ungraded:
      e.ungraded_checkpoints ??
      checkpoints.filter((x) => x.status !== "evaluated").length,
    failed:
      e.known_failed_checkpoints ??
      checkpoints.filter((x) => x.correct === false).length,
    critical:
      e.known_critical_failure_checkpoints ??
      checkpoints.filter((x) => x.critical).length,
  };
};
export const outcome = (c: Obj | undefined) => {
  const e = evaluation(c);
  const findings = checkpointFindings(c);
  return e.status !== "evaluated"
    ? e.critical
      ? "Critical failure · partial"
      : findings.graded > 0 || findings.failed > 0
        ? "Partially evaluated"
        : "Not evaluated"
    : e.critical
      ? "Critical failure"
      : e.correct
        ? "Pass"
        : "Fail";
};
export function saved<T>(key: string, fallback: T): T {
  try {
    return JSON.parse(localStorage.getItem(key) || "null") ?? fallback;
  } catch {
    return fallback;
  }
}

export function promptForSuite(
  suite: Suite,
  prompts: Prompt[],
  currentId: string,
): string | null {
  const matches = (prompt: Prompt) =>
    prompt.campaign === suite.campaign &&
    prompt.language?.toLowerCase() === suite.language?.toLowerCase();
  const current = prompts.find((prompt) => prompt.id === currentId);
  if (current && matches(current)) return current.id;
  const bound = prompts.find(
    (prompt) => prompt.id === suite.prompt_id && matches(prompt),
  );
  if (bound) return bound.id;
  const candidates = prompts.filter(
    (prompt) =>
      matches(prompt) &&
      prompt.source_kind === "production-master-template" &&
      prompt.status !== "superseded",
  );
  return candidates.length === 1 ? candidates[0].id : null;
}
